import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  const [cartCount, setCartCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load cart count
    const sessionId = localStorage.getItem('session_id');
    if (sessionId) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}`)
        .then(res => res.json())
        .then(data => {
          const count = data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
          setCartCount(count);
        })
        .catch(err => console.error('Error loading cart:', err));
    }
  }, [location]);

  const openAuthModal = (tab) => {
    setAuthModalTab(tab);
    setShowAuthModal(true);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        isScrolled ? 'shadow-lg' : 'shadow-md'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-40">
              {/* Logo */}
              <Link to="/" className="flex items-center group">
                <img 
                  src="/images/junto-logo.png"
                  alt="JUNTO®" 
                  className="h-40 md:h-44 w-auto object-contain transition-transform group-hover:scale-105"
                />
              </Link>

              {/* Desktop Menu */}
              <div className="hidden lg:flex items-center justify-center space-x-8 flex-1">
                <Link
                  to="/products?category=JUNTO® Kahveler"
                  className="text-base font-bold text-gray-900 hover:text-junto-red transition-colors relative group py-2 whitespace-nowrap"
                  data-testid="nav-kahveler"
                >
                  JUNTO® Kahveler
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-junto-red group-hover:w-full transition-all duration-300"></span>
                </Link>

                <Link
                  to="/products?category=JUNTO® Sıcak Toz Ürünler"
                  className="text-base font-bold text-gray-900 hover:text-junto-red transition-colors relative group py-2 whitespace-nowrap"
                  data-testid="nav-sicak-toz"
                >
                  JUNTO® Sıcak Toz
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-junto-red group-hover:w-full transition-all duration-300"></span>
                </Link>

                <Link
                  to="/products?category=JUNTO® Çaylar"
                  className="text-base font-bold text-gray-900 hover:text-junto-red transition-colors relative group py-2 whitespace-nowrap"
                  data-testid="nav-cay"
                >
                  JUNTO® Çaylar
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-junto-red group-hover:w-full transition-all duration-300"></span>
                </Link>

                <Link
                  to="/products?category=Espresso Makineleri"
                  className="text-base font-bold text-gray-900 hover:text-junto-red transition-colors relative group py-2 whitespace-nowrap"
                  data-testid="nav-espresso"
                >
                  Espresso Makineleri
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-junto-red group-hover:w-full transition-all duration-300"></span>
                </Link>

                <Link
                  to="/contact"
                  className="text-base font-bold text-gray-900 hover:text-junto-red transition-colors relative group py-2 whitespace-nowrap"
                  data-testid="nav-contact"
                >
                  İletişim
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-junto-red group-hover:w-full transition-all duration-300"></span>
                </Link>
              </div>

              {/* Right Side - Icons */}
              <div className="hidden lg:flex items-center space-x-6">
                {/* Search Icon */}
                <button className="text-gray-900 hover:text-junto-red transition-colors">
                  <Search className="w-6 h-6" />
                </button>

                {/* Auth */}
                {isAuthenticated ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all"
                      data-testid="user-menu-button"
                    >
                      <div className="w-8 h-8 bg-junto-red text-white rounded-full flex items-center justify-center font-bold">
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-semibold text-gray-900">{user?.name?.split(' ')[0]}</span>
                    </button>
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl py-2 z-50 border border-gray-100">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm text-gray-500">Hesap</p>
                          <p className="font-semibold text-gray-900">{user?.email}</p>
                        </div>
                        <Link
                          to="/dashboard"
                          className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <User className="w-4 h-4 inline mr-2" />
                          Hesabım
                        </Link>
                        <button
                          onClick={() => {
                            logout();
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Çıkış Yap</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => openAuthModal('login')}
                      className="text-gray-900 hover:text-junto-red font-bold transition-colors"
                      data-testid="login-button"
                    >
                      Giriş Yap
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => openAuthModal('register')}
                      className="bg-junto-red hover:bg-junto-red-dark text-white font-bold px-6 py-2 rounded-lg transition-all transform hover:scale-105"
                      data-testid="register-button"
                    >
                      Kayıt Ol
                    </button>
                  </div>
                )}

                {/* Cart */}
                <Link
                  to="/cart"
                  className="relative group"
                  data-testid="nav-cart"
                >
                  <div className="w-12 h-12 bg-junto-red text-white rounded-lg flex items-center justify-center hover:bg-junto-red-dark transition-all transform hover:scale-105">
                    <ShoppingCart className="w-6 h-6" />
                  </div>
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-white text-junto-red border-2 border-junto-red text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-gray-900 p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="lg:hidden bg-white shadow-lg rounded-lg mb-4 py-4 border-t border-gray-100" data-testid="mobile-menu">
                <Link
                  to="/products?category=JUNTO® Kahveler"
                  className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  JUNTO® Kahveler
                </Link>
                <Link
                  to="/products?category=JUNTO® Sıcak Toz Ürünler"
                  className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  JUNTO® Sıcak Toz
                </Link>
                <Link
                  to="/products?category=JUNTO® Çaylar"
                  className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  JUNTO® Çaylar
                </Link>
                <Link
                  to="/products?category=Espresso Makineleri"
                  className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Espresso Makineleri
                </Link>
                <Link
                  to="/contact"
                  className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  İletişim
                </Link>
                
                <hr className="my-2" />
                
                {!isAuthenticated && (
                  <>
                    <button
                      onClick={() => {
                        openAuthModal('login');
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                    >
                      Giriş Yap
                    </button>
                    <button
                      onClick={() => {
                        openAuthModal('register');
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                    >
                      Kayıt Ol
                    </button>
                  </>
                )}
                
                {isAuthenticated && (
                  <>
                    <Link
                      to="/dashboard"
                      className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red font-semibold"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Hesabım
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 font-semibold"
                    >
                      Çıkış Yap
                    </button>
                  </>
                )}
                
                <Link
                  to="/cart"
                  className="block px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-junto-red flex items-center justify-between font-semibold"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>Sepet</span>
                  {cartCount > 0 && (
                    <span className="bg-junto-red text-white text-xs font-bold rounded-full px-2 py-1">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </div>
            )}
        </div>
      </nav>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        defaultTab={authModalTab}
      />
    </>
  );
};

export default Navbar;